
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Building2 } from 'lucide-react';
import { getHighPriorityTowns, getMediumPriorityTowns } from '@/data/townData';

const ServicesSeo: React.FC = () => {
  const highPriorityTowns = getHighPriorityTowns();
  const mediumPriorityTowns = getMediumPriorityTowns();

  return (
    <section className="py-20 bg-gradient-to-b from-white to-pkstone-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8 text-pkstone-900 text-center">
            <span className="heading-underline pb-2">Premium Landscape Services</span>
            <span className="block mt-2 text-pkgreen-600">in Greater Bridgeport & Fairfield County</span>
          </h2>
          
          <div className="prose prose-lg max-w-none text-pkstone-700 space-y-6">
            <p className="text-xl leading-relaxed">
              At PK Landscape Associates, we're proud to provide comprehensive landscape services to residential and commercial clients throughout the Greater Bridgeport area, Fairfield County, and New Haven County, Connecticut. Our team delivers exceptional results for luxury homes, estates, and commercial properties across the region.
            </p>
            
            <p>
              Our range of services addresses every aspect of your outdoor space. Whether you're looking for premium lawn maintenance to keep your property looking its best, a complete landscape redesign for your estate, or specialized commercial services like irrigation installation or snow removal, our experienced team has the skills and equipment to exceed your expectations.
            </p>
            
            <p>
              What sets us apart is our commitment to quality and customer satisfaction. We use premium materials, follow industry best practices, and pay meticulous attention to detail. Our team is thoroughly trained, professional, and dedicated to creating and maintaining beautiful landscapes that enhance your property's value and your enjoyment of outdoor living.
            </p>
          </div>

          {/* Service Area Links - High Priority Towns */}
          <div className="bg-white rounded-xl shadow-sm p-8 border-l-4 border-pkgreen-500 my-10">
            <h3 className="text-2xl font-serif font-bold mb-4 text-pkstone-900">Featured Service Areas</h3>
            <p className="text-pkstone-600 mb-6">
              We specialize in serving Connecticut's most prestigious communities with estate-level landscape care:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {highPriorityTowns.map((town) => (
                <Link
                  key={town.slug}
                  to={`/locations/${town.slug}`}
                  className="flex items-center gap-2 p-3 bg-pkstone-50 rounded-lg hover:bg-pkgreen-50 transition-colors group"
                >
                  <MapPin className="h-4 w-4 text-pkgreen-600" />
                  <span className="text-pkstone-800 group-hover:text-pkgreen-700 font-medium">
                    {town.name}, CT
                  </span>
                </Link>
              ))}
            </div>
            <Link
              to="/locations"
              className="text-pkgreen-600 hover:text-pkgreen-700 font-medium inline-flex items-center"
            >
              View All 22+ Service Areas
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          {/* Additional Service Areas */}
          <div className="bg-pkstone-50 rounded-xl p-8 mb-10">
            <h3 className="text-2xl font-serif font-bold mb-4 text-pkstone-900">Greater Bridgeport & Valley Communities</h3>
            <p className="text-pkstone-600 mb-6">
              We also proudly serve families and businesses throughout the Greater Bridgeport area:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {mediumPriorityTowns.map((town) => (
                <Link
                  key={town.slug}
                  to={`/locations/${town.slug}`}
                  className="text-pkgreen-600 hover:text-pkgreen-700 text-sm font-medium"
                >
                  {town.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Residential vs Commercial */}
          <div className="bg-white rounded-xl shadow-sm p-8 border-l-4 border-pkearth-400">
            <h3 className="text-2xl font-serif font-bold mb-4 text-pkstone-900">Residential & Commercial Services</h3>
            <p className="text-pkstone-600 mb-6">
              We understand that residential and commercial properties have different needs:
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-pkgreen-700 mb-3 text-lg">Luxury Residential</h4>
                <p className="text-pkstone-600 mb-4">
                  Our residential services focus on creating beautiful, functional spaces that reflect your personal style and enhance your home's value. We specialize in high-end estates in Westport, Darien, Easton, and Fairfield's most exclusive neighborhoods.
                </p>
                <Link
                  to="/locations"
                  className="text-pkgreen-600 hover:text-pkgreen-700 font-medium inline-flex items-center text-sm"
                >
                  Find Your Town
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
              <div>
                <h4 className="font-semibold text-pkgreen-700 mb-3 text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Commercial Properties
                </h4>
                <p className="text-pkstone-600 mb-4">
                  Our commercial services maintain your property's professional appearance while working within your budget. We serve office parks, retail centers, HOAs, and corporate campuses throughout Greater Bridgeport and Fairfield County.
                </p>
                <Link
                  to="/commercial"
                  className="text-pkgreen-600 hover:text-pkgreen-700 font-medium inline-flex items-center text-sm"
                >
                  Commercial Services
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
          
          <p className="text-center mt-10 text-xl font-medium text-pkstone-800">
            Contact us today to schedule a consultation and discover how PK Landscape Associates can transform your Connecticut property.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSeo;
